import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import whatsappApi from './whatsappApi'
import myToast from '../../Utils/MyToast'
import SessionManager from '../../Utils/SessionManager'

/**
 * ERP WhatsApp Inbox — layout & 24h lock modelled on EduOkCrm WhatsAppInbox.jsx
 * Created By Cursor
 */

const WA = '#25D366'
const WA_DARK = '#128C7E'
const WA_CHAT_BG = '#efeae2'
const WA_BUBBLE_OUT = '#d9fdd3'
const WA_BUBBLE_IN = '#ffffff'
const SESSION_MS = 24 * 60 * 60 * 1000

function formatWindowRemaining(minutes) {
  if (!minutes || minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

function computeMessagingWindowFromThread(thread) {
  const inbound = (thread || []).filter(
    (m) => m.direction === 'inbound' && (m.messageAt || m.created_at || m.message_at)
  )
  if (!inbound.length) {
    return {
      within24h: false,
      lastInboundAt: null,
      expiresAt: null,
      minutesRemaining: 0,
    }
  }
  const lastMs = Math.max(
    ...inbound.map((m) =>
      new Date(m.messageAt || m.created_at || m.message_at).getTime()
    )
  )
  const expiresMs = lastMs + SESSION_MS
  const within24h = Date.now() < expiresMs
  return {
    within24h,
    lastInboundAt: new Date(lastMs).toISOString(),
    expiresAt: new Date(expiresMs).toISOString(),
    minutesRemaining: within24h ? Math.floor((expiresMs - Date.now()) / 60000) : 0,
  }
}

function formatTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function avatarLetter(contact) {
  const s = String(contact || '').replace(/\D/g, '')
  return (s.slice(-1) || '#').toUpperCase()
}

// Created By Cursor — show filled preview when message stores { preview, body_params }
function formatInboxMessage(raw) {
  const text = String(raw || '').trim()
  if (!text) return '—'
  try {
    const parsed = JSON.parse(text)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (parsed.preview) return String(parsed.preview)
      if (Array.isArray(parsed.body_params)) {
        return parsed.body_params.join(' · ')
      }
    }
    if (Array.isArray(parsed)) return parsed.join(' · ')
  } catch (e) {
    /* plain text */
  }
  return text
}

function SessionBanner({ messagingWindow }) {
  if (!messagingWindow) return null
  const open = !!messagingWindow.within24h
  const remaining = formatWindowRemaining(messagingWindow.minutesRemaining)

  if (open) {
    return (
      <div style={bannerOpen}>
        <div style={{ fontWeight: 800, fontSize: 12, color: WA_DARK }}>
          <i className="fa fa-check-circle" style={{ marginRight: 6 }} />
          24-hour window open
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontWeight: 600 }}>
          User messaged you recently — you can send normal text replies
          {remaining ? ` · closes in ${remaining}` : ''}.
        </div>
      </div>
    )
  }

  return (
    <div style={bannerLocked}>
      <div style={{ fontWeight: 800, fontSize: 12, color: '#92400e' }}>
        <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />
        Outside 24-hour window
      </div>
      <div style={{ fontSize: 11, color: '#a16207', marginTop: 2, fontWeight: 600 }}>
        User has not messaged in the last 24 hours — you can read this conversation, but
        replying is not available here. Use Compose to send an approved template.
      </div>
    </div>
  )
}

const WhatsAppInboxPanel = () => {
  const [conversations, setConversations] = useState([])
  const [stats, setStats] = useState({ chats: 0, unread: 0, messages: 0 })
  const [selected, setSelected] = useState(null)
  const [thread, setThread] = useState([])
  const [windowInfo, setWindowInfo] = useState({
    within24h: false,
    minutesRemaining: 0,
  })
  const [reply, setReply] = useState('')
  const [search, setSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingThread, setLoadingThread] = useState(false)
  const [sending, setSending] = useState(false)
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )
  const threadEndRef = useRef(null)

  const canReply = !!windowInfo?.within24h

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const loadConversations = useCallback(async () => {
    setLoading(true)
    try {
      const res = await whatsappApi.getInboxConversations({
        search,
        unread_only: unreadOnly ? 1 : 0,
      })
      if (res.api_status === 'OK') {
        setConversations(res.data.items || [])
        setStats(res.data.stats || { chats: 0, unread: 0, messages: 0 })
      }
    } catch (e) {
      myToast.notify_error('Failed to load inbox')
    } finally {
      setLoading(false)
    }
  }, [search, unreadOnly])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [thread, selected])

  // Tick down remaining minutes while open (CRM-style)
  useEffect(() => {
    if (!selected || !windowInfo?.expiresAt || !windowInfo?.within24h) return undefined
    const id = setInterval(() => {
      setWindowInfo((prev) => {
        if (!prev?.expiresAt) return prev
        const expiresMs = new Date(prev.expiresAt).getTime()
        const within24h = Date.now() < expiresMs
        return {
          ...prev,
          within24h,
          minutesRemaining: within24h
            ? Math.floor((expiresMs - Date.now()) / 60000)
            : 0,
        }
      })
    }, 30000)
    return () => clearInterval(id)
  }, [selected, windowInfo?.expiresAt, windowInfo?.within24h])

  const openThread = async (contact) => {
    setSelected(contact)
    setReply('')
    setLoadingThread(true)
    try {
      const res = await whatsappApi.getInboxThread(contact)
      if (res.api_status === 'OK') {
        const items = res.data.items || []
        setThread(items)
        const apiWin = res.data.messagingWindow
        const computed = computeMessagingWindowFromThread(items)
        setWindowInfo(
          apiWin && typeof apiWin.within24h === 'boolean'
            ? {
                ...computed,
                ...apiWin,
                minutesRemaining:
                  apiWin.minutesRemaining ?? computed.minutesRemaining,
              }
            : computed
        )
        loadConversations()
      }
    } catch (e) {
      myToast.notify_error('Failed to load thread')
    } finally {
      setLoadingThread(false)
    }
  }

  const sendReply = async () => {
    if (!selected || !reply.trim()) return
    if (!canReply) {
      myToast.notify_error('Outside 24-hour window — use Compose for templates')
      return
    }
    setSending(true)
    try {
      const staffId = SessionManager.getStaffDetails()?.staff_id
      const res = await whatsappApi.sendInboxReply(selected, reply.trim(), staffId)
      if (res.api_status === 'OK') {
        myToast.notify_success('Reply sent')
        setReply('')
        openThread(selected)
      } else {
        myToast.notify_error(res.api_remark || 'Reply failed')
      }
    } catch (e) {
      myToast.notify_error('Reply failed')
    } finally {
      setSending(false)
    }
  }

  const markRead = async () => {
    if (!selected) return
    try {
      await whatsappApi.markInboxRead(selected)
      loadConversations()
    } catch (e) {
      /* ignore */
    }
  }

  const showList = !isNarrow || !selected
  const showThread = !isNarrow || !!selected

  const remainingLabel = formatWindowRemaining(windowInfo.minutesRemaining)

  return (
    <div>
      {/* Top actions — like CRM inbox bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
          {stats.chats || 0} chats · {stats.unread || 0} unread
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ActionBtn
            label={
              stats.unread > 0 ? `Unread (${stats.unread})` : 'Unread'
            }
            tone="unread"
            active={unreadOnly}
            onClick={() => setUnreadOnly((v) => !v)}
            icon="fa-envelope"
          />
          <ActionBtn
            label="Refresh"
            tone="refresh"
            onClick={loadConversations}
            disabled={loading || loadingThread}
            icon="fa-refresh"
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          minHeight: 'calc(100vh - 260px)',
          border: `1px solid ${WA}28`,
          borderRadius: 14,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
        }}
      >
        {/* ── Conversation list ── */}
        <div
          style={{
            width: isNarrow ? '100%' : 340,
            borderRight: isNarrow ? 'none' : `1px solid ${WA}22`,
            display: showList ? 'flex' : 'none',
            flexDirection: 'column',
            background: '#fff',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              padding: 12,
              borderBottom: `1px solid ${WA}1f`,
              background: `${WA}0a`,
            }}
          >
            <div style={{ position: 'relative' }}>
              <i
                className="fa fa-search"
                style={{
                  position: 'absolute',
                  left: 11,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: WA_DARK,
                  fontSize: 13,
                }}
              />
              <input
                className="form-control form-control-sm"
                placeholder="Search contact, message…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  borderRadius: 10,
                  paddingLeft: 32,
                  border: '1px solid #e2e8f0',
                  fontWeight: 600,
                  background: '#fff',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              <StatChip label="chats" value={stats.chats || 0} color={WA_DARK} />
              <StatChip label="unread" value={stats.unread || 0} color="#ed6c02" />
              <StatChip label="messages" value={stats.messages || 0} color="#64748b" />
            </div>
          </div>

          {loading && <div style={progressBar} />}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {!loading && conversations.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={emptyIconWrap}>
                  <i className="fa fa-comments" style={{ fontSize: 28, color: `${WA}99` }} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
                  No conversations yet
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#64748b',
                    marginTop: 6,
                    maxWidth: 240,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    fontWeight: 600,
                  }}
                >
                  Parent replies appear here via the Meta webhook
                </div>
              </div>
            ) : (
              conversations.map((c) => {
                const contact = c.contact_number
                const active = selected === contact
                const unread = Number(c.unread_count) > 0
                return (
                  <button
                    key={contact}
                    type="button"
                    onClick={() => openThread(contact)}
                    style={{
                      display: 'flex',
                      width: '100%',
                      textAlign: 'left',
                      gap: 12,
                      alignItems: 'flex-start',
                      border: 'none',
                      borderBottom: `1px solid ${WA}14`,
                      borderLeft: active ? `3px solid ${WA}` : '3px solid transparent',
                      background: active ? `${WA}1f` : '#fff',
                      padding: '12px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `${WA}24`,
                          color: WA_DARK,
                          fontWeight: 800,
                          fontSize: 15,
                        }}
                      >
                        {avatarLetter(contact)}
                      </div>
                      {unread && (
                        <span
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: '#ed6c02',
                            border: '2px solid #fff',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: unread ? 800 : 700,
                            fontSize: 13,
                            color: '#0f172a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {contact}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: unread ? 800 : 600,
                            color: unread ? WA_DARK : '#94a3b8',
                            flexShrink: 0,
                          }}
                        >
                          {formatTime(c.last_message_at || c.updated_at)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            fontSize: 12,
                            color: unread ? '#334155' : '#64748b',
                            fontWeight: unread ? 700 : 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatInboxMessage(c.last_message)}
                        </span>
                        {unread && (
                          <span
                            style={{
                              background: WA,
                              color: '#fff',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 800,
                              padding: '2px 6px',
                              minWidth: 18,
                              textAlign: 'center',
                            }}
                          >
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* ── Thread panel ── */}
        <div
          style={{
            flex: 1,
            display: showThread ? 'flex' : 'none',
            flexDirection: 'column',
            minWidth: 0,
            background: '#fff',
          }}
        >
          {!selected ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: 32,
                ...chatWallpaper,
              }}
            >
              <div
                style={{
                  width: 80,
                  height: 80,
                  marginBottom: 14,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${WA}1f`,
                  border: `2px solid ${WA}40`,
                }}
              >
                <i className="fa fa-whatsapp" style={{ fontSize: 40, color: `${WA}b3` }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: WA_DARK }}>
                Select a conversation
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#64748b',
                  marginTop: 8,
                  textAlign: 'center',
                  maxWidth: 340,
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                Incoming WhatsApp replies are recorded automatically. Pick a chat to view
                the conversation — you can reply only if they messaged within the last 24
                hours.
              </div>
            </div>
          ) : (
            <>
              {/* Thread header — CRM light green bar */}
              <div
                style={{
                  padding: '10px 16px',
                  borderBottom: `1px solid ${WA}26`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  background: `${WA}0f`,
                  flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  {isNarrow && (
                    <button
                      type="button"
                      onClick={() => setSelected(null)}
                      style={iconBtn}
                      aria-label="Back"
                    >
                      <i className="fa fa-arrow-left" />
                    </button>
                  )}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `${WA}28`,
                      color: WA_DARK,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {avatarLetter(selected)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 14,
                        color: '#0f172a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selected}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                      {canReply
                        ? `24h window open${remainingLabel ? ` · ${remainingLabel} left` : ''}`
                        : 'Outside 24-hour window'}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={markRead} style={outlineBtn}>
                  <i className="fa fa-envelope-open-o" style={{ marginRight: 6 }} />
                  Mark read
                </button>
              </div>

              {loadingThread && <div style={progressBar} />}

              <SessionBanner messagingWindow={windowInfo} />

              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px 16px',
                  ...chatWallpaper,
                }}
              >
                {thread.length === 0 && !loadingThread ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: 40,
                      color: '#64748b',
                      fontWeight: 600,
                    }}
                  >
                    No messages in this thread yet
                  </div>
                ) : (
                  thread.map((m, index) => {
                    const inbound = m.direction === 'inbound'
                    return (
                      <div
                        key={`${m.direction}-${m.id}-${index}`}
                        style={{
                          display: 'flex',
                          justifyContent: inbound ? 'flex-start' : 'flex-end',
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '78%',
                            padding: '10px 14px',
                            borderRadius: inbound
                              ? '12px 12px 12px 4px'
                              : '12px 12px 4px 12px',
                            background: inbound ? WA_BUBBLE_IN : WA_BUBBLE_OUT,
                            border: inbound
                              ? '1px solid rgba(100,116,139,0.12)'
                              : `1px solid ${WA}40`,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            fontSize: 13,
                            color: '#0f172a',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          <div>{formatInboxMessage(m.message)}</div>
                          <div
                            style={{
                              fontSize: 10,
                              color: '#64748b',
                              marginTop: 5,
                              textAlign: 'right',
                              fontWeight: 600,
                            }}
                          >
                            {formatTime(m.messageAt || m.created_at)}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={threadEndRef} />
              </div>

              {/* Composer — locked outside 24h like CRM */}
              <div
                style={{
                  padding: 12,
                  borderTop: `1px solid ${WA}26`,
                  background: `${WA}0a`,
                  flexShrink: 0,
                }}
              >
                {canReply ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <textarea
                      className="form-control form-control-sm"
                      placeholder="Type a reply…"
                      value={reply}
                      rows={2}
                      disabled={sending}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          sendReply()
                        }
                      }}
                      style={{
                        borderRadius: 12,
                        border: '1px solid #e2e8f0',
                        fontWeight: 600,
                        resize: 'none',
                        background: '#fff',
                      }}
                    />
                    <button
                      type="button"
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      title="Send reply"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: 'none',
                        background: WA,
                        color: '#fff',
                        boxShadow: `0 4px 12px ${WA}59`,
                        cursor:
                          sending || !reply.trim() ? 'not-allowed' : 'pointer',
                        opacity: sending || !reply.trim() ? 0.55 : 1,
                        flexShrink: 0,
                      }}
                    >
                      <i className="fa fa-paper-plane" />
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                      Reply unavailable — user has not messaged in the last 24 hours.
                    </div>
                    <Link to="/AdminMain/CommunicationPanel" style={outlineBtnLink}>
                      <i className="fa fa-edit" style={{ marginRight: 6 }} />
                      Send template via Compose
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function ActionBtn({ label, onClick, disabled, tone, active, icon }) {
  const tones = {
    unread: { main: '#e65100', bg: '#ed6c021a', border: '#ed6c0247' },
    refresh: { main: '#6a1b9a', bg: '#6a1b9a1a', border: '#6a1b9a47' },
  }
  const t = tones[tone] || tones.refresh
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: `1px solid ${active ? t.main : t.border}`,
        background: active ? t.bg : t.bg,
        color: t.main,
        fontWeight: 700,
        fontSize: 12,
        padding: '6px 11px',
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <i className={`fa ${icon}`} style={{ fontSize: 12 }} />
      {label}
    </button>
  )
}

function StatChip({ label, value, color }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 8,
        background: `${color}1a`,
        border: `1px solid ${color}38`,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <span style={{ color, fontWeight: 800 }}>{value}</span>
      <span style={{ color: '#64748b' }}>{label}</span>
    </span>
  )
}

const chatWallpaper = {
  backgroundColor: WA_CHAT_BG,
  backgroundImage: `radial-gradient(rgba(18,140,126,0.06) 1px, transparent 1px)`,
  backgroundSize: '18px 18px',
}

const progressBar = {
  height: 3,
  background: `linear-gradient(90deg, ${WA}, transparent)`,
  flexShrink: 0,
}

const emptyIconWrap = {
  width: 64,
  height: 64,
  margin: '0 auto 12px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${WA}1a`,
}

const bannerOpen = {
  margin: '10px 12px 0',
  padding: '8px 12px',
  borderRadius: 8,
  background: '#ecfdf5',
  border: `1px solid ${WA}55`,
}

const bannerLocked = {
  margin: '10px 12px 0',
  padding: '8px 12px',
  borderRadius: 8,
  background: '#fffbeb',
  border: '1px solid #f59e0b66',
}

const outlineBtn = {
  border: `1px solid ${WA_DARK}4d`,
  background: '#fff',
  color: WA_DARK,
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
}

const outlineBtnLink = {
  ...outlineBtn,
  flexShrink: 0,
}

const iconBtn = {
  border: 'none',
  background: 'transparent',
  color: WA_DARK,
  width: 32,
  height: 32,
  borderRadius: 8,
  cursor: 'pointer',
}

export default WhatsAppInboxPanel
